import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import useHasMobileApp from 'src/utils/use-has-mobile-app';
import { MobileNavigationStyle } from 'types/graphql';
import { Preview, PreviewData } from './preview';

enum PreviewComponent {
  homePage = 'homepage',
  mobileApp = 'mobileApp',
  productDetailsPage = 'productDetailsPage',
}

export type MobilePreviewPlatform = 'android' | 'ios';

type PreviewSwitcherProps = {
  previewData: PreviewData;
  showProductPage?: boolean;
  /** Locks the preview to the Mobile App view and hides the Homepage/Product Page toggle buttons
   *  entirely — used when the surrounding form (see ThemeFormProps.mobileOnly) is only showing
   *  mobile-app settings, so switching to a desktop preview wouldn't reflect anything editable. */
  mobileOnly?: boolean;
};

export function getMobilePreviewData(previewData: PreviewData, mobilePlatform: MobilePreviewPlatform): PreviewData {
  if (mobilePlatform === 'ios' || !previewData.mobile) {
    return previewData;
  }

  return {
    ...previewData,
    mobile: {
      ...previewData.mobile,
      headerNavigationStyle:
        !previewData.mobile.headerNavigationStyle ||
        previewData.mobile.headerNavigationStyle === MobileNavigationStyle.platformDefault
          ? MobileNavigationStyle.solid
          : previewData.mobile.headerNavigationStyle,
      bottomBarStyle:
        !previewData.mobile.bottomBarStyle ||
        previewData.mobile.bottomBarStyle === MobileNavigationStyle.platformDefault
          ? MobileNavigationStyle.solid
          : previewData.mobile.bottomBarStyle,
    },
  };
}

export const PreviewSwitcher = ({
  previewData,
  showProductPage = true,
  mobileOnly = false,
}: PreviewSwitcherProps): JSX.Element => {
  const [activePreview, setActivePreview] = useState(
    mobileOnly ? PreviewComponent.mobileApp : PreviewComponent.homePage
  );
  const hasMobileApp = useHasMobileApp();
  const [mobilePlatform, setMobilePlatform] = useState<MobilePreviewPlatform>('ios');
  const mobilePreviewData = getMobilePreviewData(previewData, mobilePlatform);

  return (
    <>
      {!mobileOnly && (
        <ButtonContainer>
          <Button
            onClick={() => setActivePreview(PreviewComponent.homePage)}
            $isActive={activePreview === PreviewComponent.homePage}
          >
            Homepage
          </Button>
          {showProductPage && (
            <Button
              onClick={() => setActivePreview(PreviewComponent.productDetailsPage)}
              $isActive={activePreview === PreviewComponent.productDetailsPage}
            >
              Product Page
            </Button>
          )}
          {hasMobileApp && (
            <Button
              onClick={() => setActivePreview(PreviewComponent.mobileApp)}
              $isActive={activePreview === PreviewComponent.mobileApp}
            >
              Mobile App
            </Button>
          )}
        </ButtonContainer>
      )}

      {mobileOnly && (
        <ButtonContainer role='group' aria-label='Mobile preview platform'>
          <Button onClick={() => setMobilePlatform('ios')} $isActive={mobilePlatform === 'ios'}>
            iOS
          </Button>
          <Button onClick={() => setMobilePlatform('android')} $isActive={mobilePlatform === 'android'}>
            Android
          </Button>
        </ButtonContainer>
      )}

      {activePreview === PreviewComponent.homePage && <Preview.HomePage data={previewData} />}
      {showProductPage && activePreview === PreviewComponent.productDetailsPage && (
        <Preview.ProductDetailsPage data={previewData} />
      )}
      {activePreview === PreviewComponent.mobileApp && (
        <Preview.HomePage asMobileApp data={mobilePreviewData} mobilePlatform={mobilePlatform} />
      )}
    </>
  );
};

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
`;

const Button = styled.button<{ $isActive: boolean }>`
  background: #fff;
  border: 2px solid #e0e6ec;
  border-right-width: 0;
  color: #42505d;
  font-size: 12px;
  font-weight: 500;
  min-width: 124px;
  padding: 8px;
  text-transform: uppercase;

  &:first-child {
    border-radius: 50px 0 0 50px;
  }

  &:last-child {
    border-radius: 0 50px 50px 0;
    border-right-width: 2px;
  }

  ${({ $isActive }) =>
    $isActive
      ? css`
          background: #e0e6ec;
          color: #2e3538;
          font-weight: 700;
        `
      : css`
          cursor: pointer;
        `}
`;
